import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import ArrowBack from '@mui/icons-material/ArrowBack';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  createOnlineItem,
  listItems,
  listOnlineItems,
  getCategories,
  getGroupTypes,
} from '../../../endpoints/chapterSupplies';
import { getDocumentList } from '../../../endpoints/documents';
import type { CreateOnlineItemRequest } from '../../../types/chapterSupplies';
import type { DocumentList } from '../../../types/document';

function selectedNames(
  ids: string[],
  options: { id: string; name: string }[],
  maxShow = 3
): string {
  if (!ids.length) return 'None';
  const names = ids
    .map((id) => options.find((o) => o.id === id)?.name)
    .filter(Boolean) as string[];
  if (names.length > maxShow) {
    return `${names.slice(0, maxShow).join(', ')} +${names.length - maxShow} more`;
  }
  return names.join(', ');
}

const initialForm: CreateOnlineItemRequest = {
  item_id: '',
  price: 0,
  is_featured: 0,
  is_active: 1,
  short_description: null,
  sort_order: 0,
  library_document_id: null,
  max_quantity_per_order: null,
  category_ids: [],
  sub_category_ids: [],
  group_type_ids: [],
};

const DOCUMENT_SEARCH_DEBOUNCE_MS = 300;

export default function OnlineItemCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateOnlineItemRequest>(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [documentSearchInput, setDocumentSearchInput] = useState('');
  const [documentSearch, setDocumentSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(
      () => setDocumentSearch(documentSearchInput),
      DOCUMENT_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [documentSearchInput]);

  const { data: itemsData } = useApiGet(
    ['chapter-supplies-items', 500, 0],
    () => listItems({ limit: 500, offset: 0 }),
    { enabled: true, retry: 1 }
  );
  const { data: onlineItemsData } = useApiGet(
    ['chapter-supplies-online-items', 500, 0],
    () => listOnlineItems({ limit: 500, offset: 0 }),
    { enabled: true, retry: 1 }
  );
  const { data: categories = [] } = useApiGet(
    ['chapter-supplies-categories'],
    getCategories,
    { enabled: true, retry: 1 }
  );
  const { data: groupTypes = [] } = useApiGet(
    ['chapter-supplies-group-types'],
    getGroupTypes,
    { enabled: true, retry: 1 }
  );
  const { data: documentsData } = useApiGet(
    ['library-documents-search', documentSearch],
    () =>
      getDocumentList({
        search: documentSearch || undefined,
        per_page: 50,
        order: 'asc',
        sort_by: 'name',
      }),
    { enabled: true, retry: 1 }
  );
  const libraryDocuments: DocumentList[] = documentsData?.data ?? [];

  const allSubcategories = useMemo(
    () => categories.flatMap((c) => c.subcategories ?? []),
    [categories]
  );

  const availableItems = useMemo(() => {
    const items = itemsData?.items ?? [];
    const onlineItemIds = new Set(
      (onlineItemsData?.online_items ?? []).map((o) => o.item_id)
    );
    return items.filter((i) => !onlineItemIds.has(i.id));
  }, [itemsData?.items, onlineItemsData?.online_items]);

  const { mutate: submitOnlineItem, isPending } = useApiSend(
    (data: CreateOnlineItemRequest) => createOnlineItem(data),
    (response) => {
      navigate(`/chapter-supplies-management/online-items/${response.id}`);
    },
    (err) => {
      setError(err?.message ?? 'Failed to create online item');
    },
    [['chapter-supplies-online-items']]
  );

  const handleChange = (
    field: keyof CreateOnlineItemRequest,
    value: string | number | null | undefined | string[]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value ?? null }));
    setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.item_id?.trim()) {
      setError('Item is required');
      return;
    }
    if (form.price < 0) {
      setError('Price must be 0 or greater');
      return;
    }
    const payload: CreateOnlineItemRequest = {
      item_id: form.item_id.trim(),
      price: Number(form.price),
      is_featured: form.is_featured ?? 0,
      is_active: form.is_active ?? 1,
      short_description: form.short_description?.trim() || null,
      sort_order: form.sort_order ?? 0,
      library_document_id: form.library_document_id?.trim() || null,
      max_quantity_per_order:
        form.max_quantity_per_order != null
          ? Number(form.max_quantity_per_order)
          : null,
      category_ids: form.category_ids ?? [],
      sub_category_ids: form.sub_category_ids ?? [],
      group_type_ids: form.group_type_ids ?? [],
    };
    submitOnlineItem(payload);
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title="Create online item"
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() =>
                navigate('/chapter-supplies-management/online-items')
              }
            >
              Back to list
            </Button>
          }
          rightDom={undefined}
          subtitle={undefined}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="item-id-label">Item</InputLabel>
                <Select
                  labelId="item-id-label"
                  label="Item"
                  value={form.item_id ?? ''}
                  onChange={(e) =>
                    handleChange('item_id', e.target.value || undefined)
                  }
                >
                  <MenuItem value="">
                    <em>Select an item (not yet online)</em>
                  </MenuItem>
                  {availableItems.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                      {item.sku ? ` (${item.sku})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {availableItems.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  All items already have an online listing, or no items exist
                  yet.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Price"
                value={form.price === 0 ? '' : form.price}
                onChange={(e) =>
                  handleChange(
                    'price',
                    e.target.value === '' ? 0 : Number(e.target.value)
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_featured === 1}
                    onChange={(e) =>
                      handleChange('is_featured', e.target.checked ? 1 : 0)
                    }
                  />
                }
                label="Featured"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_active === 1}
                    onChange={(e) =>
                      handleChange('is_active', e.target.checked ? 1 : 0)
                    }
                  />
                }
                label="Active (visible in store)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Short description"
                value={form.short_description ?? ''}
                onChange={(e) =>
                  handleChange('short_description', e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 1 }}
                label="Sort order"
                value={form.sort_order ?? ''}
                onChange={(e) =>
                  handleChange(
                    'sort_order',
                    e.target.value === '' ? 0 : Number(e.target.value)
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete<DocumentList>
                fullWidth
                options={libraryDocuments}
                getOptionLabel={(opt) => opt.name}
                value={
                  form.library_document_id
                    ? (libraryDocuments.find(
                        (d) => d.id === form.library_document_id
                      ) ??
                      ({
                        id: form.library_document_id,
                        name: form.library_document_id,
                      } as DocumentList))
                    : null
                }
                onChange={(_, newValue) =>
                  handleChange('library_document_id', newValue?.id ?? null)
                }
                onInputChange={(_, value) => setDocumentSearchInput(value)}
                filterOptions={(x) => x}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Library document"
                    placeholder="Search documents..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 1 }}
                label="Max quantity per order"
                value={
                  form.max_quantity_per_order != null
                    ? String(form.max_quantity_per_order)
                    : ''
                }
                onChange={(e) =>
                  handleChange(
                    'max_quantity_per_order',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="categories-label">Categories</InputLabel>
                <Select
                  labelId="categories-label"
                  label="Categories"
                  multiple
                  value={form.category_ids ?? []}
                  onChange={(e) =>
                    handleChange('category_ids', e.target.value as string[])
                  }
                  renderValue={(selected) =>
                    selectedNames(selected as string[], categories)
                  }
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="subcategories-label">Subcategories</InputLabel>
                <Select
                  labelId="subcategories-label"
                  label="Subcategories"
                  multiple
                  value={form.sub_category_ids ?? []}
                  onChange={(e) =>
                    handleChange('sub_category_ids', e.target.value as string[])
                  }
                  renderValue={(selected) =>
                    selectedNames(selected as string[], allSubcategories)
                  }
                >
                  {allSubcategories.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="group-types-label">Group types</InputLabel>
                <Select
                  labelId="group-types-label"
                  label="Group types"
                  multiple
                  value={form.group_type_ids ?? []}
                  onChange={(e) =>
                    handleChange('group_type_ids', e.target.value as string[])
                  }
                  renderValue={(selected) =>
                    selectedNames(selected as string[], groupTypes)
                  }
                >
                  {groupTypes.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={isPending || !form.item_id?.trim() || form.price < 0}
              >
                {isPending ? 'Creating…' : 'Create online item'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
}
